import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Client, Deal, clientsAPI, dealsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import TrainingStatus from '../training/TrainingStatus';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import {
  StatCard,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Avatar,
  EmptyState,
  Skeleton,
  SkeletonCard,
  PageTransition,
  StaggerContainer,
  fadeInUpVariant,
} from '../ui';

// 3D Assets
// @ts-ignore
import dashboardHero from '../../assets/images/dashboard-hero.png';
// @ts-ignore
import iconUsers from '../../assets/images/icon-3d-users-v2.png';
// @ts-ignore
import iconDeals from '../../assets/images/icon-3d-deals-v2.png';
// @ts-ignore
import iconRevenue from '../../assets/images/icon-3d-revenue-v2.png';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalClients: 0,
    totalDeals: 0,
    totalRevenue: 0,
    myDeals: 0,
  });
  const [recentClients, setRecentClients] = useState<Client[]>([]);
  const [recentDeals, setRecentDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [clientsData, deals] = await Promise.all([
        clientsAPI.getAll({ limit: 1000 }),
        dealsAPI.getAll(),
      ]);

      const clients = Array.isArray(clientsData) ? clientsData : clientsData.data || [];
      const totalRevenue = deals.reduce((sum, deal) => sum + parseFloat(deal.amount), 0);
      const myDeals = deals.filter(deal => deal.userId === user?.id).length;

      setStats({
        totalClients: clients.length,
        totalDeals: deals.length,
        totalRevenue,
        myDeals,
      });

      setRecentClients(clients.slice(0, 5));
      setRecentDeals(deals.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImportanceBadge = (importance: string) => {
    const variantMap: Record<string, 'neutral' | 'primary' | 'warning' | 'error' | 'success'> = {
      VERY_LOW: 'neutral',
      LOW: 'primary',
      MEDIUM: 'warning',
      HIGH: 'error',
      VERY_HIGH: 'error',
    };

    return (
      <Badge variant={variantMap[importance] || 'neutral'} size="sm">
        {importance.replace('_', ' ')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
        <div className="mb-8">
          <Skeleton variant="text" className="w-64 h-8 mb-2" />
          <Skeleton variant="text" className="w-96 h-4" />
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <PageTransition className="px-4 sm:px-6 lg:px-8 py-8 min-h-screen bg-neutral-50/50" data-tour="dashboard-content">

      {/* Hero Section - Bento Style */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 relative overflow-hidden rounded-3xl bg-neutral-900 text-white shadow-2xl"
        >
          {/* Background Gradients */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-500/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary-500/20 rounded-full blur-[100px] translate-y-1/4 -translate-x-1/4"></div>

          <div className="relative z-10 px-8 py-10 flex flex-col justify-center h-full">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="px-2.5 py-0.5 rounded-full bg-white/10 text-xs font-semibold text-white/90 border border-white/10 backdrop-blur-md">
                v2.0 Dashboard
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 leading-tight text-white">
              Ready to break <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 break-words">new records?</span>
            </h1>
            <div className="max-w-[280px] sm:max-w-xs mb-8">
              <p className="text-lg text-neutral-200 leading-relaxed font-light">
                Good afternoon, <span className="font-semibold text-white">{user?.firstName || user?.email || 'Sales User'}</span>.
                <br />You have <span className="text-white font-semibold">{stats.myDeals} active deals</span>
                <br />requiring your attention.
              </p>
            </div>
            <div className="flex gap-4">
              <Link to="/deals/new">
                <button className="btn btn-primary bg-white text-neutral-900 hover:bg-neutral-100 border-none">
                  <PlusIcon className="w-5 h-5" />
                  New Deal
                </button>
              </Link>
            </div>
          </div>

          {/* Floating 3D Trophy */}
          <motion.div
            className="absolute right-0 bottom-0 top-0 w-1/2 hidden md:flex items-center justify-center pointer-events-none"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            <img src={dashboardHero + '?v=3'} alt="Trophy" className="w-full max-w-[320px] object-contain drop-shadow-2xl" />
          </motion.div>
        </motion.div>

        {/* Quick Actions / Mini Stats */}
        <div className="lg:col-span-1 grid grid-rows-2 gap-6">
          <div className="rounded-3xl bg-white p-6 border border-neutral-100 shadow-sm flex flex-col justify-center relative overflow-hidden group">
            <div className="absolute right-0 top-0 p-6 opacity-50 group-hover:scale-110 transition-transform duration-500">
              <img src={iconDeals + '?v=3'} alt="Deals" className="w-24 h-24 object-contain" />
            </div>
            <h3 className="text-neutral-500 font-medium text-sm mb-1">My Performance</h3>
            <div className="text-4xl font-bold text-neutral-900 mb-2">{stats.myDeals}</div>
            <div className="text-sm text-green-600 font-medium flex items-center gap-1">
              <ArrowTrendingUpIcon className="w-4 h-4" />
              <span>Top 10% of team</span>
            </div>
          </div>

          <div className="rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-600 p-6 text-white shadow-lg relative overflow-hidden">
            <div className="relative z-10">
              <h3 className="text-white/80 font-medium text-sm mb-1">Total Revenue</h3>
              <div className="text-3xl font-bold mb-4">${stats.totalRevenue.toLocaleString()}</div>
              <Link to="/deals" className="text-sm font-semibold bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg backdrop-blur-sm transition-colors border border-white/10">
                View Analytics
              </Link>
            </div>
            <div className="absolute -right-4 -bottom-4 opacity-30">
              <img src={iconRevenue + '?v=3'} alt="Revenue" className="w-32 h-32 object-contain rotate-12" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Stats Row */}
      <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <motion.div variants={fadeInUpVariant}>
          <StatCard
            title="Total Clients"
            value={stats.totalClients}
            icon={<img src={iconUsers + '?v=3'} alt="Users" className="w-full h-full object-contain" />}
            iconBackground="bg-blue-50/50"
            className="h-full border-neutral-100"
          />
        </motion.div>
        <motion.div variants={fadeInUpVariant}>
          <StatCard
            title="Active Deals"
            value={stats.totalDeals}
            icon={<img src={iconDeals + '?v=3'} alt="Deals" className="w-full h-full object-contain" />}
            iconBackground="bg-purple-50/50"
            className="h-full border-neutral-100"
          />
        </motion.div>
        <motion.div variants={fadeInUpVariant}>
          <StatCard
            title="Company Revenue"
            value={`$${stats.totalRevenue.toLocaleString()}`}
            icon={<img src={iconRevenue + '?v=3'} alt="Revenue" className="w-full h-full object-contain" />}
            iconBackground="bg-green-50/50"
            className="h-full border-neutral-100"
            trend={{ value: '+24%', direction: 'up' }}
          />
        </motion.div>
      </StaggerContainer>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Clients */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="h-full border-neutral-200/60 shadow-sm">
            <CardHeader
              title="New Clients"
              className="px-6 pt-6 mb-2"
              action={
                <Link to="/clients" className="text-sm font-semibold text-primary-600 hover:text-primary-700">See all</Link>
              }
            />
            <CardBody className="p-0">
              {recentClients.length > 0 ? (
                <div className="divide-y divide-neutral-50">
                  {recentClients.map((client) => (
                    <Link key={client.id} to={`/clients/${client.id}`} className="block hover:bg-neutral-50/80 transition-colors px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Avatar name={client.name} size="md" className="bg-primary-100 text-primary-700" />
                          <div>
                            <div className="text-sm font-bold text-neutral-900">{client.name}</div>
                            <div className="text-xs text-neutral-500">{client.email}</div>
                          </div>
                        </div>
                        {getImportanceBadge(client.importance)}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-6">
                  <EmptyState type="users" title="No clients" description="Add your first client" />
                </div>
              )}
            </CardBody>
          </Card>
        </motion.div>

        {/* Recent Deals */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="h-full border-neutral-200/60 shadow-sm">
            <CardHeader
              title="Recent Activity"
              className="px-6 pt-6 mb-2"
              action={
                <Link to="/deals" className="text-sm font-semibold text-primary-600 hover:text-primary-700">See all</Link>
              }
            />
            <CardBody className="p-0">
              {recentDeals.length > 0 ? (
                <div className="divide-y divide-neutral-50">
                  {recentDeals.map((deal) => (
                    <Link key={deal.id} to={`/deals/${deal.id}`} className="block hover:bg-neutral-50/80 transition-colors px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-600">
                            <span className="font-bold text-xs">$</span>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-neutral-900">{deal.amount} {deal.currency}</div>
                            <div className="text-xs text-neutral-500">{deal.client?.name}</div>
                          </div>
                        </div>
                        <Badge variant="neutral" size="sm" className="bg-neutral-50">
                          {deal.stage?.name || 'New'}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-6">
                  <EmptyState type="deals" title="No deals" description="Create a deal" />
                </div>
              )}
            </CardBody>
          </Card>
        </motion.div>
      </div>
    </PageTransition>
  );
};

export default Dashboard;
